import type { WorkspaceRiskCatalogItem, WorkspaceRiskListItem } from "@unbox/api";
import { Check, ChevronDown, ClockRewind, Lock01, Stars02 } from "@untitledui/icons";
import { useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import {
  buildRiskGroupsFromCatalog,
  GROUP_ICONS,
  type RiskGroup,
  type RiskItem,
  SEVERITY_META,
  SEVERITY_ORDER,
  severityToTier,
  tierToSeverity,
} from "@/components/safety-config/risk-groups";
import { cx } from "@/utils/cx";
import { type RiskSelection, type StartingPoint, startingRisks } from "./risk-setup";
import { ChoiceCard, DarkInfoCard } from "./wizard-ui";

export type DetectionValue = { startingPoint: StartingPoint; risks: RiskSelection[] };

const CORE_GROUP = "baseline";

const itemKeys = (item: RiskItem) => item.keys ?? [item.key];

const SeverityPicker = ({ tier, onChange }: { tier: number; onChange: (tier: number) => void }) => {
  const severity = tierToSeverity(tier);
  return (
    <Dropdown.Root>
      <AriaButton
        aria-label="Severity"
        className="flex items-center gap-2 rounded-md border border-primary bg-white px-2.5 py-1 text-xs font-medium outline-none cursor-pointer shrink-0"
      >
        <span className="size-2 rounded-full" style={{ backgroundColor: SEVERITY_META[severity].color }} />
        {SEVERITY_META[severity].label}
        <ChevronDown aria-hidden="true" className="size-3.5 text-quaternary" />
      </AriaButton>
      <Dropdown.Popover>
        <Dropdown.Menu
          selectedKeys={new Set([severity])}
          onSelectionChange={(keys) => {
            const value = [...keys][0] as (typeof SEVERITY_ORDER)[number] | undefined;
            if (value) onChange(severityToTier(value));
          }}
        >
          {SEVERITY_ORDER.map((option) => (
            <Dropdown.Item key={option} id={option} textValue={SEVERITY_META[option].label}>
              <span className="flex items-center gap-2">
                <span className="size-2 rounded-full" style={{ backgroundColor: SEVERITY_META[option].color }} />
                {SEVERITY_META[option].label}
              </span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
};

export const countSelectedRisks = (catalog: WorkspaceRiskCatalogItem[], risks: RiskSelection[]): number => {
  const selected = new Set(risks.map((risk) => risk.riskId));
  return buildRiskGroupsFromCatalog(catalog.filter((risk) => risk.category !== "custom"))
    .flatMap((group) => group.items)
    .filter((item) => itemKeys(item).some((key) => selected.has(key))).length;
};

const RiskCheckbox = ({ checked, locked }: { checked: boolean; locked?: boolean }) => (
  <span
    className={cx(
      "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border",
      checked ? "border-brand-950 bg-brand-950" : "border-primary bg-white",
      locked && "opacity-60",
    )}
    aria-hidden="true"
  >
    {checked && <Check className="size-3.5 text-white" strokeWidth={3} />}
  </span>
);

const ActiveCount = ({ active, total }: { active: number; total: number }) => (
  <span
    className={cx(
      "flex items-center gap-1 text-sm whitespace-nowrap",
      active > 0 ? "font-semibold text-success-700" : "text-gray-500",
    )}
  >
    {active > 0 && <Check className="size-4" />}
    {active} of {total} risks active
  </span>
);

type RowProps = {
  item: RiskItem;
  selection: Map<string, number>;
  isDefault: boolean;
  showSeverity: boolean;
  locked?: boolean;
  onToggle: (item: RiskItem, on: boolean) => void;
  onTier: (item: RiskItem, tier: number) => void;
};

const RiskRow = ({ item, selection, isDefault, showSeverity, locked, onToggle, onTier }: RowProps) => {
  const tier = itemKeys(item)
    .map((key) => selection.get(key))
    .find((value) => value !== undefined);
  const checked = tier !== undefined;
  return (
    <div className="flex items-start gap-3 border-b border-secondary px-5 py-4 last:border-b-0">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={item.name}
        disabled={locked}
        onClick={() => onToggle(item, !checked)}
        className="flex flex-1 items-start gap-3 text-left disabled:cursor-default"
      >
        <RiskCheckbox checked={checked} locked={locked} />
        <span className="flex flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-primary">
            {item.name}
            {isDefault && !showSeverity && !locked && (
              <Badge type="pill-color" color="success" size="sm">
                Common Risk
              </Badge>
            )}
          </span>
          <span className="text-sm text-tertiary">{item.desc}</span>
        </span>
      </button>
      {showSeverity && checked && !locked && (
        <SeverityPicker tier={tier ?? severityToTier(item.defaultSeverity)} onChange={(next) => onTier(item, next)} />
      )}
    </div>
  );
};

const CoreSafetyCard = ({
  group,
  detailed,
  rowProps,
}: {
  group: RiskGroup;
  detailed: boolean;
  rowProps: Omit<RowProps, "item">;
}) => (
  <div className="overflow-hidden rounded-xl border border-secondary bg-white">
    <div className="flex items-center justify-between gap-3 border-b border-success-100 bg-gradient-to-r from-success-50 to-success-25 px-5 py-3.5">
      <span className="flex items-center gap-2 text-sm font-semibold text-primary">
        <Lock01 className="size-4 text-secondary" />
        Core Safety is Enabled
      </span>
      <ActiveCount active={group.items.length} total={group.items.length} />
    </div>
    {detailed ? (
      group.items.map((item) => <RiskRow key={item.key} item={item} {...rowProps} locked />)
    ) : (
      <div className="flex flex-col gap-3 px-5 py-4">
        <p className="text-sm text-tertiary">
          These baseline safety risks, mapped to WFA/GARM, are monitored for every brand by default.
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {group.items.map((item) => (
            <li key={item.key} className="flex items-center gap-2 text-sm text-secondary">
              <Check className="size-4 text-success-600" />
              {item.name}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const RiskGroupCard = ({
  group,
  collapsible,
  rowProps,
  isDefault,
}: {
  group: RiskGroup;
  collapsible: boolean;
  rowProps: Omit<RowProps, "item" | "isDefault">;
  isDefault: (item: RiskItem) => boolean;
}) => {
  const [isOpen, setIsOpen] = useState(!collapsible);
  const Icon = GROUP_ICONS[group.id];
  const active = group.items.filter((item) => itemKeys(item).some((key) => rowProps.selection.has(key))).length;
  const expanded = !collapsible || isOpen;

  return (
    <div className="overflow-hidden rounded-xl border border-secondary bg-white">
      <button
        type="button"
        disabled={!collapsible}
        aria-expanded={expanded}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-gray-50 disabled:hover:bg-transparent disabled:cursor-default"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-primary">
          {Icon && <Icon className="size-4 text-secondary" aria-hidden="true" />}
          {group.name}
        </span>
        <span className="flex items-center gap-2">
          <ActiveCount active={active} total={group.items.length} />
          {collapsible && (
            <ChevronDown
              className={cx("size-4 text-quaternary transition-transform duration-150", isOpen && "rotate-180")}
            />
          )}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-secondary">
          {group.items.map((item) => (
            <RiskRow key={item.key} item={item} {...rowProps} isDefault={isDefault(item)} />
          ))}
        </div>
      )}
    </div>
  );
};

export const StepDetectRisks = ({
  catalog,
  workspaceRisks,
  value,
  onChange,
  showErrors,
}: {
  catalog: WorkspaceRiskCatalogItem[];
  workspaceRisks: WorkspaceRiskListItem[];
  value: DetectionValue | null;
  onChange: (value: DetectionValue) => void;
  showErrors: boolean;
}) => {
  const hasWorkspaceSafety = workspaceRisks.length > 0;
  const groups = buildRiskGroupsFromCatalog(catalog.filter((risk) => risk.category !== "custom"));
  const coreGroup = groups.find((group) => group.id === CORE_GROUP);
  const otherGroups = groups.filter((group) => group.id !== CORE_GROUP);
  const catalogById = new Map(catalog.map((risk) => [risk.id, risk]));
  const selection = new Map((value?.risks ?? []).map((risk) => [risk.riskId, risk.tier]));
  const isReuse = value?.startingPoint === "workspace";

  const choose = (startingPoint: StartingPoint) =>
    onChange({ startingPoint, risks: startingRisks(catalog, workspaceRisks, startingPoint) });

  const update = (next: Map<string, number>) => {
    if (!value) return;
    onChange({ ...value, risks: [...next].map(([riskId, tier]) => ({ riskId, tier })) });
  };

  const onToggle = (item: RiskItem, on: boolean) => {
    const next = new Map(selection);
    for (const key of itemKeys(item)) {
      if (on) next.set(key, catalogById.get(key)?.defaultTier ?? severityToTier(item.defaultSeverity));
      else next.delete(key);
    }
    update(next);
  };

  const onTier = (item: RiskItem, tier: number) => {
    const next = new Map(selection);
    for (const key of itemKeys(item)) if (next.has(key)) next.set(key, tier);
    update(next);
  };

  const isDefault = (item: RiskItem) => itemKeys(item).some((key) => catalogById.get(key)?.defaultEnabled);
  const extraDefaults = otherGroups.flatMap((group) => group.items).filter(isDefault).length;
  const totalRisks = groups.reduce((sum, group) => sum + group.items.length, 0);
  const activeRisks = countSelectedRisks(catalog, value?.risks ?? []);
  const reuseCount = countSelectedRisks(catalog, startingRisks(catalog, workspaceRisks, "workspace"));
  const rowProps = { selection, showSeverity: isReuse, onToggle, onTier };

  return (
    <div className="flex flex-col gap-6">
      {hasWorkspaceSafety && (
        <div className="flex flex-col gap-3">
          <h2 className="text-md font-semibold text-primary">Choose a starting point</h2>
          <div role="radiogroup" aria-label="Choose a starting point" className="flex flex-wrap gap-4">
            <ChoiceCard
              icon={ClockRewind}
              title="Reuse your brand's configuration"
              description="Start from the configuration your brand set up previously"
              badge={
                <Badge type="pill-color" color="success" size="sm">
                  {reuseCount} risks monitored
                </Badge>
              }
              selected={value?.startingPoint === "workspace"}
              isInvalid={showErrors && !value}
              onSelect={() => choose("workspace")}
            />
            <ChoiceCard
              icon={Stars02}
              title="Start from common defaults"
              description="The most common risks most companies switch on"
              selected={value?.startingPoint === "defaults"}
              isInvalid={showErrors && !value}
              onSelect={() => choose("defaults")}
            />
          </div>
        </div>
      )}

      {value && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1 max-w-xl">
            <h2 className="text-md font-semibold text-primary">
              {isReuse ? "Review your brand's configuration" : "Risks we monitor for your brand"}
            </h2>
            <p className="text-sm text-tertiary">
              {isReuse
                ? `${activeRisks} of ${totalRisks} risks are on, pulled from your brand's configuration. Look through them, untick anything that doesn't apply and change anything that doesn't feel right.`
                : `Your brand starts with ${coreGroup?.items.length ?? 0} Core Safety risks always on. We've picked ${extraDefaults} extra risks worth watching too. Turn on the risks you want monitored.`}
            </p>
          </div>

          {coreGroup && (
            <CoreSafetyCard group={coreGroup} detailed={isReuse} rowProps={{ ...rowProps, isDefault: true }} />
          )}
          {otherGroups.map((group) => (
            <RiskGroupCard
              key={`${group.id}-${value.startingPoint}`}
              group={group}
              collapsible={!isReuse}
              rowProps={rowProps}
              isDefault={isDefault}
            />
          ))}

          <DarkInfoCard icon={Stars02} title="Custom risks are added after setup">
            <p>
              These are specific to each merchant and need to be configured by our team, so you can't add them here.
            </p>
            <p>Once you've submitted this form, we'll be in touch to set them up with you.</p>
          </DarkInfoCard>
        </div>
      )}
    </div>
  );
};
